/**
 * First-run onboarding (v10.0.0): the real steps, in order. PURE.
 *
 * Every step is something the app can already do, and its action is offered
 * only once the steps it depends on are done — an "assign accounts" button with
 * no business to assign them to would be a dead control (CLAUDE.md). Adding to
 * the Home Screen has no button at all: iOS offers no way for a page to do it,
 * so that step says how, and is done when the app is opened from there.
 *
 * Sign in is listed because it IS the first step; anyone reading this card has
 * done it.
 */

export type StepKey = 'signin' | 'business' | 'bank' | 'assign' | 'rates' | 'homescreen';

export interface OnboardingInput {
  entities: ReadonlyArray<{ taxRatePpm: number | null; getRatePpm: number | null }>;
  connections: number;
  /** Every linked account's entity, or null when the list could not load. */
  accounts: ReadonlyArray<{ entityId: string | null }> | null;
  installed: boolean;
  onboardedAt: string | null;
}

export interface OnboardingStep {
  key: StepKey;
  title: string;
  detail: string;
  done: boolean;
  /** True when this step's action can be offered now. */
  ready: boolean;
}

export function onboardingSteps(input: OnboardingInput): OnboardingStep[] {
  const business = input.entities.length > 0;
  const bank = input.connections > 0;
  const accounts = input.accounts ?? [];
  const assign =
    input.accounts != null && accounts.length > 0 && accounts.every((a) => a.entityId != null);
  const rates =
    business && input.entities.every((e) => e.taxRatePpm != null || e.getRatePpm != null);

  return [
    {
      key: 'signin',
      title: 'Sign in',
      detail: 'Done — you are signed in.',
      done: true,
      ready: false,
    },
    {
      key: 'business',
      title: 'Add your business',
      detail: 'Its name and state. Every account and envelope belongs to one business.',
      done: business,
      ready: !business,
    },
    {
      key: 'bank',
      title: 'Connect a bank',
      detail: 'Read-only. Ballast never moves money between your accounts.',
      done: bank,
      ready: !bank,
    },
    {
      key: 'assign',
      title: 'Assign your accounts',
      detail: 'Choose which business each account belongs to. Unassigned accounts count nowhere.',
      done: assign,
      ready: !assign && business && bank && input.accounts != null && accounts.length > 0,
    },
    {
      key: 'rates',
      title: 'Set the tax and GET rates',
      detail:
        'How much of each income deposit to suggest setting aside. Leave GET blank if you do not owe it.',
      done: rates,
      ready: !rates && business,
    },
    {
      key: 'homescreen',
      title: 'Add Ballast to your Home Screen',
      detail: input.installed
        ? 'Done — notifications can reach you. Turn them on in Settings.'
        : 'In Safari, tap Share, then Add to Home Screen, and open Ballast from there. Notifications only work from the Home Screen.',
      done: input.installed,
      ready: false,
    },
  ];
}

/** The card shows until every step is done or the operator hides it. */
export function showOnboarding(input: OnboardingInput): boolean {
  if (input.onboardedAt != null) return false;
  return onboardingSteps(input).some((s) => !s.done);
}
